import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { useMinWidth } from '@/shared/hooks/useMinWidth'

const STORAGE_KEY = 'sitrepp.nav.expanded'

/**
 * Below this the labelled nav would squeeze the trips page under its working width
 * (about 620px with the trips list open), so the nav stays a rail.
 */
const EXPANDABLE_MIN_WIDTH = 1280

interface AppNavContextValue {
  /** Whether the nav is showing labels right now. */
  expanded: boolean
  /** Whether this window is wide enough to offer the labelled nav at all. */
  canExpand: boolean
  toggle: () => void
}

const AppNavContext = createContext<AppNavContextValue | null>(null)

export function AppNavProvider({ children }: { children: ReactNode }) {
  const [wantsExpanded, setWantsExpanded] = useState(() => {
    if (typeof window === 'undefined') return false
    try {
      return window.localStorage.getItem(STORAGE_KEY) === 'true'
    } catch {
      return false
    }
  })
  const canExpand = useMinWidth(EXPANDABLE_MIN_WIDTH)

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(wantsExpanded))
    } catch {
      // ignore
    }
  }, [wantsExpanded])

  return (
    <AppNavContext.Provider
      value={{
        expanded: wantsExpanded && canExpand,
        canExpand,
        toggle: () => setWantsExpanded((prev) => !prev),
      }}
    >
      {children}
    </AppNavContext.Provider>
  )
}

export function useAppNav(): AppNavContextValue {
  const ctx = useContext(AppNavContext)
  if (!ctx) throw new Error('useAppNav must be used within AppNavProvider')
  return ctx
}
